/**
 * Velocity by hour: today's bars against the last 7 days' average and its
 * normal range, for new orders, new-sale dollars, payment starts, visitors or
 * spend. Shared by the Velocity page and the Dashboard, so the two cannot
 * disagree.
 *
 * New orders and their dollars stack by molecule, visitors by channel and
 * spend by ad network (lib/channels colours), so a change in the total shows
 * which part of it moved. The average line and band stay on the total.
 *
 * `data` is the /velocity payload; `reaches` from lib/velocity reachesFor.
 */

import { useState, useMemo } from 'react'
import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'

import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from '../ui'
import { Notice } from '../scienceModel/parts'
import { hourlyRows, productSeriesFor } from '../../lib/velocity'
import { channelColor } from '../../lib/channels'

const CHART_METRICS = [
  { key: 'paid', label: 'New Orders', count: true },
  // Checkouts only, as New Orders: a renewal batch is not selling.
  { key: 'revenue', label: 'New sales $', count: false, money: true },
  { key: 'payment_initiated', label: 'Payment started', count: true },
  { key: 'visitors', label: 'Visitors', count: true },
  { key: 'spend', label: 'Spend', count: false, money: true },
]

const one = v => (v == null ? '—' : v < 10 ? v.toFixed(1) : Math.round(v).toLocaleString())
const money = v => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`)

export default function HourlyChart({ data, reaches, title = 'By hour', action = null }) {
  const [metricKey, setMetricKey] = useState('paid')
  const metric = CHART_METRICS.find(m => m.key === metricKey)
  const series = data.series[metricKey]
  const rows = useMemo(
    () => (series ? hourlyRows(series, data.days, reaches[metricKey], { count: metric.count }) : []),
    [series, data.days, reaches, metricKey, metric.count]
  )
  const fmt = metric.money ? money : one

  // The parts today's bar is stacked from, each a { day: hours[] } grid.
  const stacks = useMemo(() => {
    if (metricKey === 'paid' || metricKey === 'revenue') {
      return productSeriesFor(metricKey)
        .filter(p => data.series[p.key])
        .map(p => ({ key: p.key, label: p.label, color: p.color, grid: data.series[p.key] }))
    }
    if (metricKey === 'visitors') {
      // Channel is the visitor's first pageview of the day, as in the Channels
      // table. Biggest over the whole span first, so the stack order holds from
      // hour to hour; spend-only channels have an all-zero grid and drop out.
      const total = grid => Object.values(grid || {}).flat().reduce((a, b) => a + (b || 0), 0)
      return Object.entries(data.channels || {})
        .map(([channel, c]) => ({ channel, grid: c.visitors, total: total(c.visitors) }))
        .filter(c => c.total > 0)
        .sort((a, b) => b.total - a.total)
        .map(({ channel, grid }, i) => ({ key: `visitors_${channel}`, label: channel, color: channelColor(channel, i), grid }))
    }
    if (metricKey === 'spend') {
      return Object.entries(data.channels || {})
        .filter(([, c]) => c.spend)
        .map(([channel, c], i) => ({ key: `spend_${channel}`, label: channel, color: channelColor(channel, i), grid: c.spend }))
    }
    return []
  }, [metricKey, data])

  const chartRows = useMemo(() => {
    if (!stacks.length) return rows
    return rows.map(row => {
      const out = { ...row }
      for (const stack of stacks) {
        // Same rule as the total: nothing drawn for an hour not yet reached.
        out[stack.key] = row.today == null ? null : (stack.grid?.[data.today]?.[row.hour] ?? 0)
      }
      return out
    })
  }, [rows, stacks, data.today])

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription className="text-xs">Bars are today. The line is what today should do: {data.baseline === 'weekday' ? 'the same weekday over the last 4 weeks' : 'the last 7 days'}, scaled to today&apos;s level so far. The band is the middle 95% around it. The last bar may be a partial hour.</CardDescription>
          </div>
          {action}
        </div>
        <div className="flex flex-wrap items-center gap-1 pt-2">
          {CHART_METRICS.map(m => (
            <Button key={m.key} size="sm" variant={m.key === metricKey ? 'default' : 'outline'} onClick={() => setMetricKey(m.key)} disabled={!data.series[m.key]}>
              {m.label}
            </Button>
          ))}
          {stacks.length > 0 && (
            <div className="ml-auto flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {stacks.map(stack => (
                <span key={stack.key} className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: stack.color }} />
                  {stack.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!series ? (
          <Notice>No data for this measure.</Notice>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartRows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={1} />
                <YAxis tick={{ fontSize: 11 }} width={44} tickFormatter={v => (metric.money ? `$${Math.round(v)}` : v)} />
                <Tooltip
                  formatter={(value, name) => (Array.isArray(value) ? `${fmt(value[0])}–${fmt(value[1])}` : fmt(value))}
                  labelFormatter={label => `${label} ET`}
                />
                <Area dataKey="band" name="Normal range" stroke="none" fill="hsl(var(--muted-foreground))" fillOpacity={0.15} isAnimationActive={false} />
                {stacks.length
                  ? stacks.map((stack, i) => (
                    <Bar
                      key={stack.key}
                      dataKey={stack.key}
                      name={stack.label}
                      stackId="today"
                      fill={stack.color}
                      radius={i === stacks.length - 1 ? [2, 2, 0, 0] : 0}
                      isAnimationActive={false}
                    />
                  ))
                  : <Bar dataKey="today" name="Today" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} isAnimationActive={false} />}
                <Line dataKey="expected" name="7-day average" stroke="hsl(var(--muted-foreground))" strokeDasharray="4 3" dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
        {metricKey === 'spend' && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            Spend is Meta + Google, shown only for hours both have reported. Google&apos;s figures arrive through a BigQuery transfer that runs hours behind Google Ads, so a Google hour can include spend from earlier in the day. Reddit spend is not ingested.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
